// Country display normalization. The data layer is a mess of mixed
// representations (ISO-2 codes in warehouses, full names in customers,
// postal codes mis-mapped in suppliers from the CIN7 port). This helper
// renders a coherent display string regardless of what's in the column.
//
// Strategy:
//   - ISO-2 codes (2 letters, all uppercase OR uppercased here) → expand
//     to "Full Name (CODE)". Falls back to just "CODE" if name unknown.
//   - Full names (>= 4 letters, first letter capitalized) → keep as-is.
//   - Garbage (digits, "UNKNOWN", "GD. 523085" type postal mess, blank) →
//     return '' so callers can render '—'.
//
// Usage:
//   import { formatCountry, isCountryGarbage } from './countryFormat';
//   <Typography>{formatCountry(row.country) || '—'}</Typography>

// Curated mapping covering everything we currently see in production +
// common ISO-2 codes. Add more as the customer/supplier corpus grows.
const ISO2_TO_NAME = {
  AU: 'Australia',
  CA: 'Canada',
  CN: 'China',
  CZ: 'Czech Republic',
  DE: 'Germany',
  ES: 'Spain',
  EU: 'European Union',
  FR: 'France',
  GB: 'United Kingdom',
  HK: 'Hong Kong',
  ID: 'Indonesia',
  IE: 'Ireland',
  IN: 'India',
  IT: 'Italy',
  JP: 'Japan',
  KR: 'South Korea',
  MY: 'Malaysia',
  NL: 'Netherlands',
  NO: 'Norway',
  NZ: 'New Zealand',
  PH: 'Philippines',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SE: 'Sweden',
  SG: 'Singapore',
  CH: 'Switzerland',
  TH: 'Thailand',
  TW: 'Taiwan',
  UK: 'United Kingdom', // technically not ISO-2 but used in our data
  US: 'United States',
  VN: 'Vietnam',
};

// Reverse map (lowercased name → ISO-2-ish). Used when we want to coerce a
// full name into the canonical code form.
const NAME_TO_ISO2 = {};
for (const [code, name] of Object.entries(ISO2_TO_NAME)) {
  NAME_TO_ISO2[name.toLowerCase()] = code;
}
// Extra aliases for common variants:
NAME_TO_ISO2['united states'] = 'US';
NAME_TO_ISO2['united states of america'] = 'US';
NAME_TO_ISO2['united kingdom'] = 'UK';
NAME_TO_ISO2['great britain'] = 'UK';
NAME_TO_ISO2['england'] = 'UK';
NAME_TO_ISO2['holland'] = 'NL';

// Garbage detector — postal codes / digit blocks / "GD. xxx" / "UNKNOWN".
export const isCountryGarbage = (raw) => {
  if (raw === null || raw === undefined) return true;
  const s = String(raw).trim();
  if (!s) return true;
  if (s.toUpperCase() === 'UNKNOWN') return true;
  // Anything starting with a digit or punctuation is junk.
  if (/^[\d.\-_/]/.test(s)) return true;
  // Very short non-letter tokens.
  if (s.length < 2) return true;
  // Mostly digits anywhere.
  const nonAlpha = (s.match(/[^A-Za-z\s]/g) || []).length;
  if (nonAlpha > s.length / 3) return true;
  return false;
};

// Returns the human display form: "Full Name" if we know it, "CODE" if it
// looks like an unknown ISO-2 code, '' for junk/blank.
export const formatCountry = (raw) => {
  if (isCountryGarbage(raw)) return '';
  const s = String(raw).trim();
  // ISO-2: exactly 2 letters
  if (/^[A-Za-z]{2}$/.test(s)) {
    const up = s.toUpperCase();
    return ISO2_TO_NAME[up] || up;
  }
  // Otherwise treat as a full name. Normalize obvious caps weirdness.
  return s.replace(/\s+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Returns the canonical storage form (ISO-2 code) if derivable, else ''.
// Useful for migration scripts that want to coerce mixed values.
export const toIso2 = (raw) => {
  if (isCountryGarbage(raw)) return '';
  const s = String(raw).trim();
  if (/^[A-Za-z]{2}$/.test(s)) {
    const up = s.toUpperCase();
    return ISO2_TO_NAME[up] ? up : up; // unknown ISO-2 still canonicalised
  }
  return NAME_TO_ISO2[s.toLowerCase()] || '';
};

export default formatCountry;
